import React, { useContext, useState, useCallback, useRef } from 'react';
import { Context } from './Context';
import { Sidebar as NativeSidebar } from '../components/native/Sidebar';
import { Sidebar as MuiSidebar } from '../components/mui/Sidebar';
import { Sidebar as ShadcnSidebar, SidebarProvider, SidebarInset } from '../components/shadcn/Sidebar';
import { Assistant as NativeAssistant } from '../components/native/Assistant';
import { Assistant as MuiAssistant } from '../components/mui/Assistant';
import { Assistant as ShadcnAssistant } from '../components/shadcn/Assistant';
import { Header } from './Header';
import type { AssistantMessage, FileMetadata } from '../design/components';
import type { ChatMessage } from '../design/services';

/**
 * Layout - Header at top, Sidebar on left side, content on right
 */
export const Layout = ({ children }: { children: React.ReactNode }) => {
  const [context] = useContext(Context)!
  const design = context?.project?.design;
  const Sidebar = design === 'shadcn' ? ShadcnSidebar : design === 'mui' ? MuiSidebar : NativeSidebar;
  const Assistant = design === 'shadcn' ? ShadcnAssistant : design === 'mui' ? MuiAssistant : NativeAssistant;
  const [collapsed, setCollapsed] = useState(true);
  // Open assistant by default on wide screens — fills the extra horizontal space
  // instead of leaving the main content stretched edge-to-edge.
  const [assistantOpen, setAssistantOpen] = useState(() =>
    typeof window !== 'undefined' && window.innerWidth >= 1400
  );
  const [messages, setMessages] = useState<AssistantMessage[]>([]);
  const messagesRef = useRef(messages);
  messagesRef.current = messages;
  const [isLoading, setIsLoading] = useState(false);

  const handleAssistantToggle = () => {
    setAssistantOpen(prev => {
      if (!prev) setCollapsed(true); // Auto-collapse nav when opening assistant
      return !prev;
    });
  };

  const handleSend = useCallback(async (message: string, files?: File[]) => {
    if (!context?.llm) return [];

    setIsLoading(true);
    const messageId = `assistant-${Date.now()}`;

    // Build FileMetadata from attached files
    const fileMetadata: FileMetadata[] = (files ?? []).map(f => ({
      url: URL.createObjectURL(f),
      name: f.name,
      type: f.type,
      size: f.size,
      modified: new Date(f.lastModified).toISOString(),
      pages: f.type === 'application/pdf' ? 0 : 1,
      file: f,
    }));

    // Set files on AI instance for the update tool
    context.llm.files = fileMetadata;

    // Add user message
    const userMessage: AssistantMessage = {
      id: `user-${Date.now()}`,
      role: 'user',
      content: message,
      timestamp: new Date(),
      ...(fileMetadata.length > 0 ? { files: [...fileMetadata] } : {}),
    };

    // Add empty assistant message that we'll stream into
    const assistantMessage: AssistantMessage = {
      id: messageId,
      role: 'assistant',
      content: '',
      timestamp: new Date(),
      streaming: true,
    };
    setMessages(prev => [...prev, userMessage, assistantMessage]);

    try {
      // Build conversation history (exclude the empty streaming message)
      const chatMessages: ChatMessage[] = [
        ...messagesRef.current.filter(m => m.id !== messageId).map(m => ({
          role: m.role,
          content: m.content
        })),
        { role: 'user', content: message }
      ];

      // Stream the LLM response (system prompt + tools handled by client.ts)
      for await (const event of context.llm.chat({ messages: chatMessages })) {
        if (event.type === 'text') {
          setMessages(prev => prev.map(m =>
            m.id === messageId ? { ...m, content: m.content + event.text } : m
          ));
        }
      }
      // Mark streaming complete
      setMessages(prev => prev.map(m =>
        m.id === messageId ? { ...m, streaming: false } : m
      ));
    } catch (error: unknown) {
      console.error('AI chat error:', error);
      let errorContent = 'Sorry, I encountered an error. Please try again.';
      if (error instanceof Error) {
        try {
          // Extract JSON from message (may be prefixed with status code like "400 {...}")
          const jsonMatch = error.message.match(/\{[\s\S]*\}/);
          if (jsonMatch) {
            const parsed = JSON.parse(jsonMatch[0]);
            if (parsed?.error?.message) {
              errorContent = parsed.error.message;
            }
          }
        } catch {
          errorContent = error.message;
        }
      }
      // Update the message with error
      setMessages(prev => prev.map(m =>
        m.id === messageId ? { ...m, content: errorContent, error: true, streaming: false } : m
      ));
    } finally {
      setIsLoading(false);
    }
    // Return unused files back to the attachment area
    return context.llm.files.filter((f): f is NonNullable<typeof f> => f != null).map(f => f.file).filter((f): f is File => f != null);
  }, [context?.llm]);

  // Auto-greet when assistant opens for the first time.
  // Wait for the project's Firebase config to populate — on wide screens the
  // assistant opens immediately at mount, before the authn subscription has
  // set context.project, and sending without a project would hit the wrong
  // run() endpoint (CORS-fail on a child hostname).
  const projectConfigReady = !!(context?.project?.config as { projectId?: string } | undefined)?.projectId;
  React.useEffect(() => {
    if (assistantOpen && messages.length === 0 && projectConfigReady) {
      handleSend('Hi');
    }
  }, [assistantOpen, messages.length, projectConfigReady, handleSend]);

  // Assistant panel — keep mounted so open/close animates via width transition
  const assistantPanel = (
    <aside style={{
      width: assistantOpen ? '360px' : '0',
      flexShrink: 0,
      overflow: 'hidden',
      transition: 'width 0.2s',
    }}>
      <div style={{ width: '360px', height: '100%', borderRight: '1px solid rgba(0,0,0,0.08)' }}>
        <Assistant state={[messages, setMessages]} onSend={handleSend} disabled={isLoading} />
      </div>
    </aside>
  );

  // Shadcn design - SidebarProvider at top level
  if (design === 'shadcn') {
    return (
      <div style={{ display: 'flex', height: '100vh', overflow: 'hidden' }}>
        {assistantPanel}
        <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          <SidebarProvider defaultOpen={!collapsed} open={!collapsed} onOpenChange={(open) => setCollapsed(!open)} className="flex-1 !min-h-0">
            <Sidebar onAssistantToggle={handleAssistantToggle} assistantOpen={assistantOpen} />
            <SidebarInset className="min-w-0 overflow-hidden">
              <Header />
              <main className="main-content" style={{ overflow: 'auto', flex: 1, minHeight: 0 }}>
                {children}
              </main>
            </SidebarInset>
          </SidebarProvider>
        </div>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', height: '100vh' }}>
      {assistantPanel}
      <div style={{ display: 'flex', flexDirection: 'column', flex: 1, minWidth: 0 }}>
        <Header />
        <div style={{ display: 'flex', flex: 1, minHeight: 0 }}>
          <aside style={{ width: collapsed ? '48px' : '240px', flexShrink: 0, borderRight: '1px solid rgba(0,0,0,0.08)', transition: 'width 0.2s', overflow: 'auto' }}>
            <Sidebar state={[collapsed, setCollapsed]} onAssistantToggle={handleAssistantToggle} assistantOpen={assistantOpen} />
          </aside>
          <main className="main-content" style={{ overflow: 'auto', flex: 1 }}>
            {children}
          </main>
        </div>
      </div>
    </div>
  );
};
